import type { NotificationDto } from 'src/contracts/notification';
import type { CoffeeChatMessageDto, CoffeeChatRequestDto } from 'src/contracts/coffee-chat';

import React, { useRef, useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { useAuth } from 'src/features/auth/hooks/use-auth';
import { notificationApi } from 'src/features/notifications/api';

import { openEventStream } from 'src/lib/event-stream';
import { getAuthFromStorage } from 'src/lib/auth-storage';

// Coffee chat event types
interface CoffeeChatMessageEvent {
  requestId: string;
  message: CoffeeChatMessageDto;
}

interface CoffeeChatMessagesReadEvent {
  requestId: string;
  messageIds: string[];
}

// Event callback types
type CoffeeChatMessageCallback = (event: CoffeeChatMessageEvent) => void;
type CoffeeChatMessagesReadCallback = (event: CoffeeChatMessagesReadEvent) => void;
type CoffeeChatStatusChangedCallback = (request: CoffeeChatRequestDto) => void;
type CoffeeChatUnreadCountCallback = (count: number) => void;

interface NotificationContextType {
  notifications: NotificationDto[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  // Coffee chat real-time
  coffeeChatUnreadCount: number;
  subscribeToCoffeeChatMessages: (callback: CoffeeChatMessageCallback) => () => void;
  subscribeToCoffeeChatMessagesRead: (callback: CoffeeChatMessagesReadCallback) => () => void;
  subscribeToCoffeeChatStatusChanged: (callback: CoffeeChatStatusChangedCallback) => () => void;
  subscribeToCoffeeChatUnreadCount: (callback: CoffeeChatUnreadCountCallback) => () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  // Coffee chat state
  const [coffeeChatUnreadCount, setCoffeeChatUnreadCount] = useState<number>(0);

  // Coffee chat event subscribers (using refs to avoid re-renders)
  const coffeeChatMessageCallbacks = useRef<Set<CoffeeChatMessageCallback>>(new Set());
  const coffeeChatMessagesReadCallbacks = useRef<Set<CoffeeChatMessagesReadCallback>>(new Set());
  const coffeeChatStatusChangedCallbacks = useRef<Set<CoffeeChatStatusChangedCallback>>(new Set());
  const coffeeChatUnreadCountCallbacks = useRef<Set<CoffeeChatUnreadCountCallback>>(new Set());

  // Get token from auth storage - will be used in the connection
  const getToken = useCallback(() => {
    const authData = getAuthFromStorage();
    return authData?.token || '';
  }, []);

  // Fetch notifications function - defined before it's used in the effect
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const response = await notificationApi.getNotifications();
      if (response.isSuccess && response.items) {
        setNotifications(response.items);
        setUnreadCount(response.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Live events: one stream per signed-in session. It reconnects by itself; what was
  // missed while it was down is picked up by the next fetch.
  useEffect(() => {
    if (!isAuthenticated || !getToken()) {
      return () => {};
    }

    return openEventStream({
      getToken,
      handlers: {
        ReceiveNotification: (notification: NotificationDto) => {
          setNotifications((prev) => [notification, ...prev]);
        },
        UpdateUnreadCount: (count: number) => {
          setUnreadCount(count);
        },
        ReceiveCoffeeChatMessage: (event: CoffeeChatMessageEvent) => {
          coffeeChatMessageCallbacks.current.forEach((callback) => callback(event));
        },
        CoffeeChatMessagesRead: (event: CoffeeChatMessagesReadEvent) => {
          coffeeChatMessagesReadCallbacks.current.forEach((callback) => callback(event));
        },
        CoffeeChatStatusChanged: (request: CoffeeChatRequestDto) => {
          coffeeChatStatusChangedCallbacks.current.forEach((callback) => callback(request));
        },
        CoffeeChatUnreadCountUpdate: (count: number) => {
          setCoffeeChatUnreadCount(count);
          coffeeChatUnreadCountCallbacks.current.forEach((callback) => callback(count));
        },
      },
    });
  }, [isAuthenticated, getToken]);

  // Fetch initial notifications on mount and auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, fetchNotifications]);

  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const response = await notificationApi.markAsRead(notificationId);
      if (response.isSuccess && response.data) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await notificationApi.markAllAsRead();
      if (response.isSuccess && response.data) {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Coffee chat subscription functions
  const subscribeToCoffeeChatMessages = useCallback((callback: CoffeeChatMessageCallback) => {
    coffeeChatMessageCallbacks.current.add(callback);
    return () => {
      coffeeChatMessageCallbacks.current.delete(callback);
    };
  }, []);

  const subscribeToCoffeeChatMessagesRead = useCallback((callback: CoffeeChatMessagesReadCallback) => {
    coffeeChatMessagesReadCallbacks.current.add(callback);
    return () => {
      coffeeChatMessagesReadCallbacks.current.delete(callback);
    };
  }, []);

  const subscribeToCoffeeChatStatusChanged = useCallback((callback: CoffeeChatStatusChangedCallback) => {
    coffeeChatStatusChangedCallbacks.current.add(callback);
    return () => {
      coffeeChatStatusChangedCallbacks.current.delete(callback);
    };
  }, []);

  const subscribeToCoffeeChatUnreadCount = useCallback((callback: CoffeeChatUnreadCountCallback) => {
    coffeeChatUnreadCountCallbacks.current.add(callback);
    return () => {
      coffeeChatUnreadCountCallbacks.current.delete(callback);
    };
  }, []);

  // Create memoized context value
  const contextValue = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      markAsRead,
      markAllAsRead,
      fetchNotifications,
      coffeeChatUnreadCount,
      subscribeToCoffeeChatMessages,
      subscribeToCoffeeChatMessagesRead,
      subscribeToCoffeeChatStatusChanged,
      subscribeToCoffeeChatUnreadCount,
    }),
    [
      notifications,
      unreadCount,
      loading,
      markAsRead,
      markAllAsRead,
      fetchNotifications,
      coffeeChatUnreadCount,
      subscribeToCoffeeChatMessages,
      subscribeToCoffeeChatMessagesRead,
      subscribeToCoffeeChatStatusChanged,
      subscribeToCoffeeChatUnreadCount,
    ]
  );

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  );
};

export const useNotificationProvider = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationProvider must be used within a NotificationProvider');
  }
  return context;
};
